import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { getFIMActivitiesInformation as getFIMDetailsService} from 'services/modules/soc/agent';

import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, TablePagination, Tooltip, Button, MenuItem, FormControl, Select, InputLabel } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import Breadcrumbs from 'config/Breadcrumb';

const ViewFIMData = () => {
  // React router
  const navigate = useNavigate();

  // Redux dispatch
  const dispatch = useDispatch();

  // use location hook
  const location = useLocation();
  
  // State variables
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPath, setCurrentPath] = useState(['Security Alerts']);
  const severityOptions = ["all", "low", "medium", "high", "critical"];
  const [severity, setSeverity] = useState("all");
  const queryParams = new URLSearchParams(location.search);
  const agentName = queryParams.get('agentname');
  const agentId = queryParams.get('agentid');
  
  // Redux selectors
  const response = useSelector(state => state.socData.fimActivities);
  const selectedCompanyContainer = useSelector(state => state.auth.companyData);
  
  // Gets Severity Label based on rule level
  const getSeverityLabel = useCallback((ruleLevel) => {
    if (ruleLevel >= 0 && ruleLevel <= 6) return "Low";
    if (ruleLevel >= 7 && ruleLevel <= 8) return "Medium";
    if (ruleLevel >= 9 && ruleLevel <= 11) return "High";
    if (ruleLevel >= 12) return "Critical";
    return "";
  }, []);
  
  // Fetches FIM Data based on agent id
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = { agentId, pagestart: page * rowsPerPage, limit: rowsPerPage, severity, soc_env: selectedCompanyContainer?.soc_env };
        dispatch(getFIMDetailsService({ params }));
        setCurrentPath(['Dashboard', 'File Integrity Monitoring', agentName]);
      } finally {
        setLoading(false);
      }
    };
    if (agentId) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, page, rowsPerPage, severity,agentName,selectedCompanyContainer?.soc_env]);
  
  // use effect will convert the time to desired format
  useEffect(() => {
    if (response?.data?.rawResponse) {
      const hits = response.data.rawResponse.hits.hits;
      setTotalCount(response.data.rawResponse.hits.total);

      const groupedData = hits.map(hit => {
        const utcDate = new Date(hit._source.timestamp);
        const istDate = new Date(utcDate.getTime() + 5.5 * 60 * 60 * 1000);
        return {
          timestamp: istDate.toLocaleString('en-GB', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
          }).replace(',', '').replace(' at', ' @'),
          agentName: hit._source.agent.name,
          ruleName: hit._source.rule.id,
          ruleDescription: hit._source.rule.description,
          ruleLevel: hit._source.rule.level,
          alertId: hit._id,
          agentip: hit._source.agent.ip,
          mitreid: hit._source?.rule?.mitre?.id?.[0],
          mitreTactics: hit._source?.rule?.mitre?.tactic?.join(', ') || "N/A"
        };
      });
      setData(groupedData);
    }
  }, [response]);
  
  // Handle Change in Page
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  // Navigates to view fim alerts
  const viewAlerts = (alert) => {
    navigate('/soc/view-security-alert', {
      state: { alertId: alert.alertId, name: 'fim', timestamp: alert.timestamp, severity: getSeverityLabel(alert.ruleLevel) }
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className='p-4'>
      <Breadcrumbs paths={currentPath} />
      <Typography variant="h5" align="center">View Alerts</Typography>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <FormControl variant="outlined" fullWidth size="small">
          <InputLabel>Severity</InputLabel>
          <Select value={severity} label="Severity" onChange={(event) => { setSeverity(event.target.value); setPage(0); }}>
            {severityOptions.map(sev => <MenuItem key={sev} value={sev}>{sev.charAt(0).toUpperCase() + sev.slice(1)}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" className="back font" style={{ position: 'relative', bottom: '5px' }} onClick={() => window.history.back()}>Back</Button>
      </div>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align='center'>Incident Time</TableCell>
              <TableCell align='center'>Agent Name</TableCell>
              <TableCell align='center'>Agent IP</TableCell>
              <TableCell align='center'>Technique ID</TableCell>
              <TableCell align='center'>Tactics</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align='center'>Severity</TableCell>
              <TableCell align='center'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center"><WarningIcon color="disabled" fontSize="small" />No Data Available</TableCell>
              </TableRow>
            ) : (
              data.map((alert, index) => (
                <TableRow key={index}>
                  <TableCell align='center' style={{ color: '#4e4ece' }}><b>{alert.timestamp}</b></TableCell>
                  <TableCell align='center'>{alert.agentName}</TableCell>
                  <TableCell align='center' style={{ color: '#fc659c' }}><b>{alert.agentip}</b></TableCell>
                  <TableCell align='center'><b>{alert.mitreid}</b></TableCell>
                  <TableCell align="center">{alert.mitreTactics}</TableCell>
                  <TableCell>{alert.ruleDescription?.length > 70 ? alert.ruleDescription.slice(0, 70) + '...' : alert.ruleDescription}</TableCell>
                  <TableCell align='center'>{getSeverityLabel(alert.ruleLevel)}</TableCell>
                  <TableCell align='center'><Tooltip title="View"><img src="/assets/viewicon.svg" alt="View" className='icons-style' onClick={() => viewAlerts(alert)} /></Tooltip></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination rowsPerPageOptions={[5, 7, 10, 15, 20, 25, 50]} component="div" count={totalCount} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
    </div>
  );
};

export default ViewFIMData;
